import React, { useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Spinner,
  Center,
  useColorMode,
} from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { getColor, getComponent } from '../../../brandConfig';
import { CaptureUserDetailsModal } from './CaptureUserDetailsModal';
import { useAuthState, usePhoneInput, useAuthVerification, useUsernameAuth } from '../hooks';
import { PhoneInputForm } from './PhoneInputForm';
import { EmailInputForm } from './EmailInputForm';
import { CodeVerificationForm } from './CodeVerificationForm';
import { UsernamePasswordForm } from './UsernamePasswordForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { COUNTRY_CODES, type AuthMethod } from '../types';
import { GET_TENANT_AUTH_CONFIG } from '../queries';

interface UnifiedLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (token: string) => void;
  // Legacy prop names for backward compatibility
  onLoginSuccess?: () => void;
}

/**
 * Unified Login Modal Component
 *
 * Provides passwordless authentication via:
 * - SMS verification (preferred for Australian mobile numbers)
 * - Email verification (fallback option)
 *
 * Features:
 * - Country-specific phone formatting
 * - Automatic user details capture for incomplete profiles
 * - Dark mode support
 * - Accessibility compliant
 *
 * @example
 * ```tsx
 * <UnifiedLoginModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   onSuccess={(token) => {
 *     localStorage.setItem('auth_token', token);
 *     navigate('/dashboard');
 *   }}
 * />
 * ```
 */
export const UnifiedLoginModal: React.FC<UnifiedLoginModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onLoginSuccess, // Legacy prop for backward compatibility
}) => {
  // Fetch tenant auth configuration
  const { data: tenantData, loading: configLoading, error: configError } = useQuery(GET_TENANT_AUTH_CONFIG, {
    fetchPolicy: 'cache-first',
  });

  // Track whether we've applied the tenant config at least once
  const configApplied = useRef(false);

  // Extract auth config with defaults
  // Note: tenantAuthConfig is a public query that returns the config directly (not nested under currentTenant)
  const authConfig = useMemo(() => ({
    enablePhoneAuth: tenantData?.tenantAuthConfig?.enablePhoneAuth ?? true,
    enableEmailAuth: tenantData?.tenantAuthConfig?.enableEmailAuth ?? true,
    enableUsernamePassword: tenantData?.tenantAuthConfig?.enableUsernamePassword ?? false,
    defaultAuthMethod: (tenantData?.tenantAuthConfig?.defaultAuthMethod || 'phone') as AuthMethod,
    discreteAlternativeAuth: tenantData?.tenantAuthConfig?.discreteAlternativeAuth ?? false,
  }), [tenantData]);

  // Color mode support
  const { colorMode } = useColorMode();

  // Define theme-aware colors
  const cardBg = getColor(colorMode === 'light' ? "background.card" : "background.darkSurface", colorMode);
  const textPrimary = getColor(
    colorMode === 'light' ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === 'light' ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === 'light' ? "text.muted" : "text.mutedDark",
    colorMode
  );
  const accentBlue = getColor("primary", colorMode);
  const accentHover = getColor("primaryHover", colorMode);

  // Form component colors
  const formFieldBg = getComponent("form", "fieldBg", colorMode);
  const formFieldBorder = getComponent("form", "fieldBorder", colorMode);
  const formFieldBorderFocus = getComponent("form", "fieldBorderFocus", colorMode);
  const formLabelColor = getComponent("form", "labelColor", colorMode);

  // Authentication state management
  const {
    authMethod,
    authStep,
    selectedCountry,
    phoneNumber,
    localPhoneNumber,
    email,
    username,
    password,
    confirmPassword,
    isSignup,
    code,
    error,
    showCaptureDetails,
    verifiedUserId,
    verifiedUserData,
    pendingToken,
    setAuthStep,
    setSelectedCountry,
    setLocalPhoneNumber,
    setPhoneNumber,
    setEmail,
    setUsername,
    setPassword,
    setConfirmPassword,
    setIsSignup,
    setCode,
    setError,
    setShowCaptureDetails,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    resetState,
    switchToEmail,
    switchToPhone,
    switchToUsername,
    goToForgotPassword,
    goBackToLogin,
  } = useAuthState();

  // Phone input handling
  const { handlePhoneInput } = usePhoneInput(
    selectedCountry,
    setLocalPhoneNumber,
    setPhoneNumber
  );

  // Handle both new and legacy callback signatures
  const handleSuccess = (token: string) => {
    console.log('[FRONTEND-1] 🎉 handleSuccess called with token:', token);
    console.log('[FRONTEND-2] 📝 Token length:', token?.length);

    if (onSuccess) {
      console.log('[FRONTEND-3] 🔄 Calling onSuccess callback');
      onSuccess(token);
    } else if (onLoginSuccess) {
      console.log('[FRONTEND-3] 💾 Using legacy onLoginSuccess - storing in localStorage');
      localStorage.setItem('auth_token', token);

      // Verify it was stored
      const storedToken = localStorage.getItem('auth_token');
      console.log('[FRONTEND-4] ✅ Verification - token stored in localStorage as "auth_token":', !!storedToken);
      console.log('[FRONTEND-5] ✅ Stored token matches:', storedToken === token);

      console.log('[FRONTEND-6] 📞 Calling onLoginSuccess callback');
      onLoginSuccess();
    } else {
      console.error('[FRONTEND-3] ❌ No callback provided!');
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Username/password authentication
  const {
    usernameAvailable,
    checkingUsername,
    loading: usernameAuthLoading,
    checkUsernameAvailability,
    cancelUsernameCheck,
    handleSubmit: handleUsernameSubmit,
  } = useUsernameAuth({
    username,
    password,
    confirmPassword,
    isSignup,
    setError,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setShowCaptureDetails,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  // Set default auth method based on tenant config when modal opens
  useEffect(() => {
    console.log('🔐 [AUTH-CONFIG] Modal open:', isOpen);
    console.log('🔐 [AUTH-CONFIG] Auth config:', authConfig);

    if (!isOpen) {
      // Reset the flag when modal closes so it re-applies on next open
      configApplied.current = false;
      return;
    }

    if (isOpen && tenantData?.tenantAuthConfig) {
      const { defaultAuthMethod, enablePhoneAuth, enableEmailAuth, enableUsernamePassword } = authConfig;

      console.log('🔐 [AUTH-CONFIG] Default method:', defaultAuthMethod);
      console.log('🔐 [AUTH-CONFIG] Enabled - Phone:', enablePhoneAuth, 'Email:', enableEmailAuth, 'Username:', enableUsernamePassword);

      // Find the first enabled method to use as default
      let methodToUse: AuthMethod = 'phone';

      if (defaultAuthMethod === 'username' && enableUsernamePassword) {
        methodToUse = 'username';
      } else if (defaultAuthMethod === 'email' && enableEmailAuth) {
        methodToUse = 'email';
      } else if (defaultAuthMethod === 'phone' && enablePhoneAuth) {
        methodToUse = 'phone';
      } else if (enableUsernamePassword) {
        methodToUse = 'username';
      } else if (enableEmailAuth) {
        methodToUse = 'email';
      } else if (enablePhoneAuth) {
        methodToUse = 'phone';
      }

      console.log('🔐 [AUTH-CONFIG] Method to use:', methodToUse, 'Current method:', authMethod);

      // Apply the config method
      if (methodToUse !== authMethod) {
        if (methodToUse === 'phone') switchToPhone();
        else if (methodToUse === 'email') switchToEmail();
        else if (methodToUse === 'username') switchToUsername();
      }

      configApplied.current = true;
    }
  }, [isOpen, tenantData, authConfig]);

  // Check username availability when username changes (signup only)
  useEffect(() => {
    if (isSignup && username.length >= 3) {
      checkUsernameAvailability(username);
    }
    return () => {
      cancelUsernameCheck();
    };
  }, [username, isSignup, checkUsernameAvailability, cancelUsernameCheck]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (country) {
      setSelectedCountry(country);
      // Reformat existing number for new country
      if (localPhoneNumber) {
        handlePhoneInput(localPhoneNumber);
      }
    }
  };

  // Verification handlers
  const {
    handleSendCode,
    handleVerifyCode,
    handleResendCode,
    loading,
  } = useAuthVerification({
    authMethod,
    phoneNumber,
    localPhoneNumber,
    email,
    code,
    selectedCountry,
    setAuthStep,
    setError,
    setCode,
    setVerifiedUserId,
    setVerifiedUserData,
    setPendingToken,
    setShowCaptureDetails,
    onSuccess: handleSuccess,
    onClose: handleClose,
  });

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="md">
        <ModalOverlay />
        <ModalContent bg={cardBg} color={textPrimary}>
          <ModalHeader color={textPrimary}>
            {authStep === 'input'
              ? authMethod === 'username'
                ? isSignup
                  ? 'Create Account'
                  : 'Sign In'
                : 'Sign In'
              : authStep === 'verify'
              ? 'Verify Code'
              : authStep === 'forgot-password'
              ? 'Reset Password'
              : 'Sign In'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {/* Wait for tenant auth config before rendering form to prevent wrong method flash.
                Fall through on error so we still show a form with defaults rather than a stuck spinner. */}
            {(configLoading && !configApplied.current && !configError) ? (
              <Center py={8}>
                <Spinner size="lg" color={accentBlue} />
              </Center>
            ) : (
            <VStack spacing={4} align="stretch">
              {authStep === 'input' && (
                <>
                  {authMethod === 'phone' ? (
                    <PhoneInputForm
                      selectedCountry={selectedCountry}
                      localPhoneNumber={localPhoneNumber}
                      phoneNumber={phoneNumber}
                      error={error}
                      loading={loading}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onCountryChange={handleCountryChange}
                      onPhoneInput={handlePhoneInput}
                      onSendCode={handleSendCode}
                      onSwitchToEmail={authConfig.enableEmailAuth ? switchToEmail : undefined}
                      onSwitchToUsername={authConfig.enableUsernamePassword ? switchToUsername : undefined}
                    />
                  ) : authMethod === 'email' ? (
                    <EmailInputForm
                      email={email}
                      error={error}
                      loading={loading}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onEmailChange={setEmail}
                      onSendCode={handleSendCode}
                      onSwitchToPhone={authConfig.enablePhoneAuth ? switchToPhone : undefined}
                      onSwitchToUsername={authConfig.enableUsernamePassword ? switchToUsername : undefined}
                    />
                  ) : (
                    <UsernamePasswordForm
                      username={username}
                      password={password}
                      confirmPassword={confirmPassword}
                      isSignup={isSignup}
                      error={error}
                      loading={usernameAuthLoading}
                      usernameAvailable={usernameAvailable}
                      checkingUsername={checkingUsername}
                      discreteAlternativeAuth={authConfig.discreteAlternativeAuth}
                      formLabelColor={formLabelColor}
                      formFieldBg={formFieldBg}
                      formFieldBorder={formFieldBorder}
                      formFieldBorderFocus={formFieldBorderFocus}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      textMuted={textMuted}
                      accentBlue={accentBlue}
                      accentHover={accentHover}
                      onUsernameChange={setUsername}
                      onPasswordChange={setPassword}
                      onConfirmPasswordChange={setConfirmPassword}
                      onToggleMode={() => setIsSignup(!isSignup)}
                      onSubmit={handleUsernameSubmit}
                      onForgotPassword={goToForgotPassword}
                      onSwitchToPhone={authConfig.enablePhoneAuth ? switchToPhone : undefined}
                      onSwitchToEmail={authConfig.enableEmailAuth ? switchToEmail : undefined}
                    />
                  )}
                </>
              )}

              {authStep === 'verify' && (
                <CodeVerificationForm
                  authMethod={authMethod}
                  phoneNumber={phoneNumber}
                  email={email}
                  code={code}
                  error={error}
                  loading={loading}
                  formFieldBg={formFieldBg}
                  formFieldBorder={formFieldBorder}
                  formFieldBorderFocus={formFieldBorderFocus}
                  textPrimary={textPrimary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  accentHover={accentHover}
                  onCodeChange={setCode}
                  onVerifyCode={handleVerifyCode}
                  onResendCode={handleResendCode}
                  onChangeIdentifier={() => setAuthStep('input')}
                />
              )}

              {authStep === 'forgot-password' && (
                <ForgotPasswordForm
                  formLabelColor={formLabelColor}
                  formFieldBg={formFieldBg}
                  formFieldBorder={formFieldBorder}
                  formFieldBorderFocus={formFieldBorderFocus}
                  textPrimary={textPrimary}
                  textSecondary={textSecondary}
                  textMuted={textMuted}
                  accentBlue={accentBlue}
                  accentHover={accentHover}
                  onBack={goBackToLogin}
                  onSuccess={() => {
                    // After successful reset, go back to login
                    goBackToLogin();
                  }}
                />
              )}
            </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Capture User Details Modal - shown when user is missing fname/lname/email/phone */}
      {showCaptureDetails && verifiedUserId && (
        <CaptureUserDetailsModal
          isOpen={showCaptureDetails}
          onClose={() => {
            setShowCaptureDetails(false);
            setVerifiedUserId(null);
            setVerifiedUserData(null);
            setPendingToken(null);
          }}
          userId={verifiedUserId}
          pendingToken={pendingToken}
          currentUserData={verifiedUserData}
          onUpdateSuccess={() => {
            console.log('📝 User details captured successfully');
            setShowCaptureDetails(false);
            // Now complete the login with the pending token
            if (pendingToken) {
              handleSuccess(pendingToken);
            }
            handleClose();
          }}
        />
      )}
    </>
  );
};
